import { RoleName } from '../../src/types';
import { EdgeFeatureHubConfig } from 'featurehub-javascript-client-sdk';

export interface LocalFhClient {
  feature(featureName: FeatureFlag): { enabled: boolean };
}

export enum FeatureFlag {
  AVAILABILITY_UI = 'AVAILABILITY_UI',
  ENABLE_LOG_CERTIFICATION_UI = 'ENABLE_LOG_CERTIFICATION_UI',
  LOG_CERTIFICATION_V2 = 'LOG_CERTIFICATION_V2',
  LOG_CERTIFICATION_V3 = 'LOG_CERTIFICATION_V3',
  MISSING_LOGS_UI = 'MISSING_LOGS_UI',
  VIOLATIONS_DURATION_UI = 'VIOLATIONS_DURATION_UI',
  LINE4_AUDIT_UI = 'LINE4_AUDIT_UI',
  LINE4_AUDIT_FUEL_EXCEL_UPLOAD = 'LINE4_AUDIT_FUEL_EXCEL_UPLOAD',
  ENABLE_GET_DEVICES_FROM_EVENTS = 'ENABLE_GET_DEVICES_FROM_EVENTS',
  ENABLE_DEVICES_TABLE_VIEW = 'ENABLE_DEVICES_TABLE_VIEW',
  KEYCLOAK_MIGRATION_ENABLED = 'KEYCLOAK_MIGRATION_ENABLED',
}

const featureFlags: Record<FeatureFlag, { enabled: boolean }> = {
  [FeatureFlag.AVAILABILITY_UI]: { enabled: true },
  [FeatureFlag.ENABLE_LOG_CERTIFICATION_UI]: { enabled: false },
  [FeatureFlag.LOG_CERTIFICATION_V2]: { enabled: false },
  [FeatureFlag.LOG_CERTIFICATION_V3]: { enabled: true },
  [FeatureFlag.MISSING_LOGS_UI]: { enabled: true },
  [FeatureFlag.VIOLATIONS_DURATION_UI]: { enabled: true },
  [FeatureFlag.LINE4_AUDIT_UI]: { enabled: false },
  [FeatureFlag.LINE4_AUDIT_FUEL_EXCEL_UPLOAD]: { enabled: false },
  [FeatureFlag.ENABLE_GET_DEVICES_FROM_EVENTS]: { enabled: true },
  [FeatureFlag.ENABLE_DEVICES_TABLE_VIEW]: { enabled: false },
  [FeatureFlag.KEYCLOAK_MIGRATION_ENABLED]: { enabled: false },
};

export const mockFhClient = {
  feature(featureName: FeatureFlag) {
    return featureFlags[featureName];
  },
};

/**
 * Initialize FeatureHub anonymously (before user login)
 * This allows us to read feature flags like KEYCLOAK_MIGRATION_ENABLED before authentication
 */
export async function setupFeatureHubAnonymous(): Promise<any> {
  const fhConfig = new EdgeFeatureHubConfig('https://featurehub.telematicsplatform.tools',
    `${window.config.featureHubKey}`);

  return new Promise<any>((resolve, reject) => {
    fhConfig.addReadynessListener(readiness => {
      if (readiness === 'Ready') {
        resolve(fhConfig.repository());
      } else if (readiness === 'Failed') {
        reject(new Error('FeatureHub failed to initialize'));
      }
    });

    const context = fhConfig.newContext();
    context.build();
  });
}

/**
 * Initialize FeatureHub with authenticated user context
 * This should be called after successful login
 */
export async function setupFeatureHub(accountId: string = '', id: string = '', email: string = '',
  roles: RoleName[] = [], place: string = '') {

  try {
    let fhConfig = new EdgeFeatureHubConfig('https://featurehub.telematicsplatform.tools',
      `${window.config.featureHubKey}`);

    let context = fhConfig.newContext();

    context.attributeValues('AccountId', [accountId]);
    context.attributeValues('Roles', roles);
    context.attributeValues('UserId', [id]);
    context.attributeValues('Email', [email]);
    context.attributeValues('PlaceId', [place]);

    const fhClient = await context.build();
    const repository = fhClient.repository();

    if ((repository as any).readynessState !== 'Ready') {
      throw new Error('FeatureHub repository is not ready.');
    }

    return repository;
  } catch (err) {
    return mockFhClient;
  }
}